import { rootRef } from './firebase';

export interface PeerEvalAuditStats {
  evals: { total: number; valid: number; legacy_missing_evaluator: number; legacy_wrong_doc_id: number };
  comments: { total: number; valid: number; legacy_missing_evaluator: number; legacy_wrong_doc_id: number };
  archived: { peer_evals_legacy: number; peer_comments_legacy: number };
  flags: { seedExists: boolean; migrationExists: boolean; migrationData?: Record<string, unknown> };
  samples: {
    legacy_missing_evaluator: Array<Record<string, unknown>>;
    legacy_wrong_doc_id: Array<Record<string, unknown>>;
  };
}

function classifyEvalDoc(id: string, data: Record<string, unknown>) {
  if (!data.evaluatorId || !data.colleagueId || data.questionId == null) {
    return 'legacy_missing_evaluator' as const;
  }
  const expected = `${data.evaluatorId}_${data.colleagueId}_${data.questionId}`;
  if (id !== expected) return 'legacy_wrong_doc_id' as const;
  return 'valid' as const;
}

function classifyCommentDoc(id: string, data: Record<string, unknown>) {
  if (!data.evaluatorId || !data.colleagueId) return 'legacy_missing_evaluator' as const;
  const expected = `${data.evaluatorId}_${data.colleagueId}`;
  if (id !== expected) return 'legacy_wrong_doc_id' as const;
  return 'valid' as const;
}

/** 브라우저/앱 내부에서 실행하는 peer_evals 무결성 감사 (읽기 전용) */
export async function runPeerEvalAudit(): Promise<PeerEvalAuditStats> {
  const dataRoot = rootRef();

  const seedSnap = await dataRoot.collection('settings').doc('seed').get();
  const migrationSnap = await dataRoot.collection('settings').doc('peer_eval_v2').get();

  const evalSnap = await dataRoot.collection('peer_evals').get();
  const commentSnap = await dataRoot.collection('peer_comments').get();
  const legacyEvalSnap = await dataRoot.collection('peer_evals_legacy').get();
  const legacyCommentSnap = await dataRoot.collection('peer_comments_legacy').get();

  const evalStats = { total: evalSnap.size, valid: 0, legacy_missing_evaluator: 0, legacy_wrong_doc_id: 0 };
  const commentStats = { total: commentSnap.size, valid: 0, legacy_missing_evaluator: 0, legacy_wrong_doc_id: 0 };
  const samples = {
    legacy_missing_evaluator: [] as Array<Record<string, unknown>>,
    legacy_wrong_doc_id: [] as Array<Record<string, unknown>>,
  };

  for (const doc of evalSnap.docs) {
    const kind = classifyEvalDoc(doc.id, doc.data());
    evalStats[kind] += 1;
    if (kind !== 'valid' && samples[kind].length < 3) {
      samples[kind].push({ id: doc.id, ...doc.data() });
    }
  }

  for (const doc of commentSnap.docs) {
    const kind = classifyCommentDoc(doc.id, doc.data());
    commentStats[kind] += 1;
  }

  return {
    evals: evalStats,
    comments: commentStats,
    archived: {
      peer_evals_legacy: legacyEvalSnap.size,
      peer_comments_legacy: legacyCommentSnap.size,
    },
    flags: {
      seedExists: seedSnap.exists,
      migrationExists: migrationSnap.exists,
      migrationData: migrationSnap.exists ? migrationSnap.data() : undefined,
    },
    samples,
  };
}

export function formatAuditReport(result: PeerEvalAuditStats): string {
  const hasLegacy =
    result.evals.legacy_missing_evaluator > 0 ||
    result.evals.legacy_wrong_doc_id > 0 ||
    result.comments.legacy_missing_evaluator > 0 ||
    result.comments.legacy_wrong_doc_id > 0;

  const lines = [
    '=== Peer Eval DB Audit ===',
    `peer_evals total: ${result.evals.total}`,
    `  valid: ${result.evals.valid}`,
    `  legacy (no evaluatorId): ${result.evals.legacy_missing_evaluator}`,
    `  legacy (wrong doc id): ${result.evals.legacy_wrong_doc_id}`,
    `peer_comments total: ${result.comments.total}`,
    `  valid: ${result.comments.valid}`,
    `  legacy (no evaluatorId): ${result.comments.legacy_missing_evaluator}`,
    `  legacy (wrong doc id): ${result.comments.legacy_wrong_doc_id}`,
    `archived: evals=${result.archived.peer_evals_legacy}, comments=${result.archived.peer_comments_legacy}`,
    `seed flag: ${result.flags.seedExists}`,
    `migration flag: ${result.flags.migrationExists}`,
    hasLegacy ? '[ACTION] Legacy data found - migration recommended' : '[OK] No legacy data in active collections',
  ];
  return lines.join('\n');
}

declare global {
  interface Window {
    __auditFirestore?: () => Promise<PeerEvalAuditStats>;
  }
}
